import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useLanguage } from "@/context/AppProviders";
import { api, formatApiError } from "@/lib/api";
import { FadeIn, Overline } from "@/components/Kinetic";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForceChangePassword() {
  const { t } = useLanguage();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return <div className="pt-40 pb-40 px-10 text-ink/40">{t.common.loading}</div>;
  if (!user.must_change_password) {
    navigate(user.role === "admin" ? "/admin" : "/espace-membre", { replace: true });
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t.auth.resetMismatch);
      return;
    }
    setBusy(true);
    try {
      await api.post("/me/change-password", { current_password: "", new_password: password });
      await refreshUser();
      toast.success(t.forcePw.success);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-ink/20 focus:border-ember outline-none px-4 py-3.5 text-ink placeholder:text-ink/30 transition-colors duration-300 text-sm";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-20" data-testid="force-change-password-page">
      <FadeIn className="w-full max-w-md">
        <Overline>{t.forcePw.overline}</Overline>
        <h1 className="mt-4 font-display font-semibold tracking-tighter text-4xl text-ink flex items-center gap-3">
          <ShieldCheck size={30} className="text-ember" /> {t.forcePw.title}
        </h1>
        <p className="mt-4 text-ink/55 text-sm leading-relaxed">{t.forcePw.sub}</p>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="force-pw-form">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink/50 mb-2">{t.auth.newPassword}</label>
            <div className="relative">
              <input data-testid="force-pw-input" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className={`${inputCls} pr-12`} />
              <button type="button" data-testid="force-pw-toggle" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ember transition-colors" aria-label="toggle">
                {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink/50 mb-2">{t.auth.confirmPassword}</label>
            <input data-testid="force-pw-confirm" type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} className={inputCls} />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="force-pw-error">{error}</p>}
          <button data-testid="force-pw-submit" disabled={busy} className="btn-glow w-full bg-ink hover:bg-ember disabled:opacity-50 text-white px-6 py-4 text-sm tracking-wide transition-colors duration-300">
            {busy ? t.auth.loading : t.forcePw.submit}
          </button>
        </form>
      </FadeIn>
    </div>
  );
}
